import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { LabelItem } from '../types/LabelItem';
import { generateQRCodeContent } from '../utils/qrCodeGenerator';
import { truncateText } from '../utils/labelFormatter';
import '../styles/print.css';

interface LabelProps {
    /** Label item to display, or null for empty label slot */
    item: LabelItem | null;
    /** Debug: show cell index (optional) */
    cellIndex?: number;
}

/**
 * Label component - displays a single 3×7cm label with 2 QR codes and text.
 *
 * Each label contains two identical QR codes for redundancy (if one is damaged,
 * the duplicate provides backup) and the displayName text in readable format.
 */
export const Label: React.FC<LabelProps> = ({ item, cellIndex }) => {
    // Render empty placeholder for unused label slots
    if (!item) {
        return (
            <div className="label empty-label">
                {cellIndex !== undefined && (
                    <div
                        className="no-print"
                        style={{
                            position: 'absolute',
                            top: 2,
                            left: 2,
                            fontSize: 10,
                            color: '#999',
                        }}
                    >
                        [{cellIndex}]
                    </div>
                )}
            </div>
        );
    }

    // Generate QR code content from item data
    const qrContent = generateQRCodeContent(item.qrData);

    // Different layout for storage vs user
    const isStorage = item.type === 'storage';

    return (
        <div className="label" style={{ position: 'relative' }}>
            {cellIndex !== undefined && (
                <div
                    className="no-print"
                    style={{
                        position: 'absolute',
                        top: 2,
                        left: 2,
                        fontSize: 10,
                        color: '#999',
                    }}
                >
                    [{cellIndex}]
                </div>
            )}
            {isStorage ? (
                <>
                    {/* Storage: 1 QR code, larger */}
                    <div
                        className="label-qr-codes"
                        style={{ justifyContent: 'center' }}
                    >
                        <QRCodeSVG
                            value={qrContent}
                            size={113}
                            level="H"
                            includeMargin={false}
                        />
                    </div>
                    <div className="label-text" style={{ marginTop: 0 }}>
                        {truncateText(item.displayName)}
                    </div>
                </>
            ) : (
                <>
                    {/* User: 2 QR codes for redundancy */}
                    <div className="label-qr-codes">
                        <QRCodeSVG
                            value={qrContent}
                            size={75}
                            level="H"
                            includeMargin={false}
                        />
                        <QRCodeSVG
                            value={qrContent}
                            size={75}
                            level="H"
                            includeMargin={false}
                        />
                    </div>
                    <div className="label-text">
                        {truncateText(item.displayName)}
                    </div>
                </>
            )}
        </div>
    );
};
